import { useCallback, useMemo, useState } from 'react'

import { DefaultPopover, FormGroup, Label, Select, cn } from 'twyne-new.ui'

import { useCustomReportStore } from '../store'
import {
  AUTO_INTERVAL,
  CHART_INTERVALS,
  acceptsIntervalQuantity,
  getChartGroupingLabel,
  resolveChartIntervalQuantity,
  resolveChartDimensionField,
  resolveChartInterval,
  supportsInterval,
} from '../utils/chartIntervals'
import { collapsesChartIndex } from '../utils/chartUtils'
import {
  CHART_TYPES,
  applyChartAxis,
  applyChartType,
  getChartDimensionOptions,
  getChartTypeShortLabel,
} from '../utils/constants'

import { CHART_POPOVER_CONTENT_CLASS } from './AddChartPopover'
import IntervalQuantityInput from './IntervalQuantityInput'

function useSummaryField(chart, config) {
  return useMemo(() => {
    if (collapsesChartIndex(chart.dataType)) return chart.seriesBy || null

    return resolveChartDimensionField(chart, config)
  }, [chart, config])
}

export default function ChartDisplayPopover({ chart }) {
  const {
    config: { value: config },
    charts: { update },
  } = useCustomReportStore()

  const [open, setOpen] = useState(false)

  const summaryField = useSummaryField(chart, config)
  const collapsesIndex = collapsesChartIndex(chart.dataType)
  const [axisField] = chart.dimensions || []
  const showInterval = !collapsesIndex && supportsInterval(axisField)

  const typeLabel = getChartTypeShortLabel(chart.dataType)

  const fieldLabel = useMemo(() => {
    if (!summaryField) return collapsesIndex ? 'metrics' : ''

    return getChartGroupingLabel(chart, config)
  }, [chart, collapsesIndex, config, summaryField])

  const prefix = fieldLabel ? `${typeLabel} by` : `${typeLabel} ·`
  const triggerLabel = fieldLabel || 'pick an x axis'

  const axisOptions = useMemo(
    () => getChartDimensionOptions(config.collection, chart.dataType),
    [config.collection, chart.dataType],
  )

  const resolvedInterval = resolveChartInterval(chart, config)
  const showQuantity = showInterval && acceptsIntervalQuantity(resolvedInterval)
  const quantity = resolveChartIntervalQuantity(chart, config)

  const intervalHint = useMemo(() => {
    if (!showInterval) return ''
    if (chart.interval && chart.interval !== AUTO_INTERVAL) return ''

    const option = CHART_INTERVALS.find(item => item.value === resolvedInterval)

    return `Auto resolves to ${(option?.label || resolvedInterval || '').toLowerCase()} buckets for the selected date range.`
  }, [chart.interval, resolvedInterval, showInterval])

  const handleTypeChange = useCallback(option => {
    update(chart.id, applyChartType(chart, option?.value, config.groupby))
  }, [chart, config.groupby, update])

  const handleAxisChange = useCallback(option => {
    update(chart.id, applyChartAxis(chart, option?.value))
  }, [chart, update])

  const handleIntervalChange = useCallback(option => {
    update(chart.id, { interval: option?.value || AUTO_INTERVAL })
  }, [chart.id, update])

  const handleQuantityChange = useCallback(intervalQuantity => {
    update(chart.id, { intervalQuantity })
  }, [chart.id, update])

  return (
    <span className="flex min-w-0 items-baseline gap-1 text-sm twy-text-base-muted">
      <span className="shrink-0">{prefix}</span>
      <DefaultPopover
        title="Chart display"
        wrapperProps={{ open, onOpenChange: setOpen }}
        triggerProps={{ asChild: true }}
        contentProps={{
          className: cn(CHART_POPOVER_CONTENT_CLASS),
          align: 'start',
          collisionPadding: 12,
        }}
        trigger={
          <button
            type="button"
            className="min-w-0 truncate underline-offset-2 hover:underline hover:twy-text-base-accent"
          >
            {triggerLabel}
          </button>
        }
      >
        <div className="flex flex-col gap-4 min-w-0">
          {!collapsesIndex && <FormGroup>
            <Label>X axis</Label>
            <Select
              options={axisOptions}
              value={chart.dimensions?.[0] ?? null}
              onChange={handleAxisChange}
              placeholder="Select the x axis"
            />
          </FormGroup>}
          {showInterval && <FormGroup>
            <Label>Granularity</Label>
            <div className="flex items-center gap-2">
              {showQuantity && <IntervalQuantityInput
                value={quantity}
                onChange={handleQuantityChange}
              />}
              <div className="min-w-0 flex-1">
                <Select
                  options={CHART_INTERVALS}
                  value={chart.interval || AUTO_INTERVAL}
                  onChange={handleIntervalChange}
                />
              </div>
            </div>
            {intervalHint && <span className="mt-1 block text-xs twy-text-base-muted">
              {intervalHint}
            </span>}
          </FormGroup>}
          <FormGroup>
            <Label>Type</Label>
            <Select
              options={CHART_TYPES}
              value={chart.dataType}
              onChange={handleTypeChange}
            />
          </FormGroup>
        </div>
      </DefaultPopover>
    </span>
  )
}
